import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { RotateCcw, Send, CheckCircle2, XCircle, Clock, AlertTriangle, Search } from "lucide-react";

const STATUS = {
  sent: { label: "Envoyé", cls: "bg-[#059669] text-white", icon: CheckCircle2 },
  not_sent_no_provider: { label: "En attente", cls: "bg-[#F59E0B] text-white", icon: Clock },
  skipped_invalid_number: { label: "Numéro invalide", cls: "bg-[#868E96] text-white", icon: XCircle },
  error: { label: "Erreur", cls: "bg-[#E60000] text-white", icon: AlertTriangle },
  pending: { label: "En cours", cls: "bg-[#0EA5E9] text-white", icon: Clock },
};
const DELIVERY = {
  delivered: { label: "Livré", color: "#059669" },
  undelivered: { label: "Non livré", color: "#E60000" },
  failed: { label: "Échec", color: "#E60000" },
  sent: { label: "Transmis", color: "#0EA5E9" },
  queued: { label: "En file Twilio", color: "#868E96" },
  accepted: { label: "Accepté", color: "#868E96" },
};
const TABS = [["all", "Tout"], ["not_sent_no_provider", "En attente"], ["sent", "Envoyés"], ["error", "Erreurs"], ["skipped_invalid_number", "Numéro invalide"]];

export const TwilioJournalTab = ({ cfg, onChanged }) => {
  const [status, setStatus] = useState("all");
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], total: 0, kinds: [] });
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const params = new URLSearchParams({ status, kind, limit: "100" });
      if (q.trim()) params.set("q", q.trim());
      const { data: d } = await api.get(`/admin/twilio/logs?${params}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, kind]);

  const resendAll = async () => {
    setBusy("all");
    try {
      const { data: r } = await api.post("/admin/twilio/resend-pending");
      toast.success(`${r.sent} SMS renvoyé(s) sur ${r.scanned}${r.errors ? ` · ${r.errors} erreur(s)` : ""}`);
      await Promise.all([load(), onChanged?.()]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const resendOne = async (id) => {
    setBusy(id);
    try {
      const { data: r } = await api.post(`/admin/twilio/logs/${id}/resend`);
      if (r.status === "sent") toast.success("SMS renvoyé"); else toast.error(`Renvoi : ${r.error || r.status}`);
      await Promise.all([load(), onChanged?.()]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="bg-white border border-[#DEE2E6]" data-testid="tw-journal-tab">
      <header className="p-4 border-b border-[#DEE2E6] flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1.5">
          {TABS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setStatus(k)} data-testid={`tw-log-filter-${k}`}
                    className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 border transition ${status === k ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-[#DEE2E6] hover:border-[#002FA7]"}`}>{l}</button>
          ))}
        </div>
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="tw-log-kind" className="h-8 border border-[#DEE2E6] bg-white px-2 text-xs">
          <option value="all">Tous les types</option>
          {(data.kinds || []).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); load(); }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Numéro, texte, SID…" className="h-8 w-48 text-xs" data-testid="tw-log-search" />
          <Button type="submit" size="sm" variant="outline" className="h-8" data-testid="tw-log-search-btn"><Search className="h-3.5 w-3.5" /></Button>
        </form>
        <div className="flex-1" />
        <Button size="sm" onClick={resendAll} disabled={busy === "all" || !cfg?.enabled || !cfg?.pending_retry} data-testid="tw-resend-all" className="h-8 bg-[#F59E0B] text-white">
          <RotateCcw className={`h-3.5 w-3.5 mr-1 ${busy === "all" ? "animate-spin" : ""}`} />Renvoyer les SMS en attente ({cfg?.pending_retry ?? 0})
        </Button>
      </header>
      {!cfg?.enabled && cfg?.pending_retry > 0 && (
        <div className="px-4 py-2 text-[11px] bg-[#FFFBEB] border-b border-[#FFD400]/40 text-[#92400E]" data-testid="tw-resend-hint">
          Les SMS en attente partiront automatiquement dès que Twilio sera configuré (puis toutes les heures en rattrapage).
        </div>
      )}
      <div className="px-4 py-2 text-[10px] text-[#868E96] uppercase tracking-wider font-bold" data-testid="tw-log-count">{data.total} SMS · {data.items.length} affichés</div>
      {data.items.length === 0 ? (
        <div className="p-10 text-center text-sm text-[#868E96]" data-testid="tw-log-empty">Aucun SMS dans le journal.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#F8F9FA] text-left text-[10px] uppercase tracking-wider">
              <tr><th className="p-3">Date</th><th className="p-3">Destinataire</th><th className="p-3">Type</th><th className="p-3">Message</th><th className="p-3">Statut</th><th className="p-3">Livraison</th><th className="p-3" /></tr>
            </thead>
            <tbody>
              {data.items.map((l) => {
                const s = STATUS[l.status] || { label: l.status, cls: "bg-[#868E96] text-white", icon: Clock };
                const d = l.delivery_status ? (DELIVERY[l.delivery_status] || { label: l.delivery_status, color: "#868E96" }) : null;
                const canResend = ["not_sent_no_provider", "error"].includes(l.status) && !l.resent_log_id;
                return (
                  <tr key={l.id} className="border-t border-[#F8F9FA] align-top" data-testid={`tw-log-${l.id}`}>
                    <td className="p-3 font-mono text-[10px] whitespace-nowrap">{new Date(l.created_at).toLocaleString("fr-FR")}</td>
                    <td className="p-3 font-mono whitespace-nowrap">{l.to}</td>
                    <td className="p-3"><span className="px-2 py-0.5 bg-[#F8F9FA] border border-[#DEE2E6] text-[10px] uppercase">{l.kind || "info"}</span></td>
                    <td className="p-3 max-w-md"><div className="line-clamp-2" title={l.body}>{l.body}</div>
                      {l.error && <div className="text-[10px] text-[#E60000] mt-0.5" data-testid={`tw-log-error-${l.id}`}>{l.error}</div>}
                      {l.resent_log_id && <div className="text-[10px] text-[#059669] mt-0.5">Renvoyé {l.resent_at ? new Date(l.resent_at).toLocaleString("fr-FR") : ""}</div>}
                    </td>
                    <td className="p-3"><span className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold px-1.5 py-0.5 ${s.cls}`} data-testid={`tw-log-status-${l.id}`}><s.icon className="h-3 w-3" />{s.label}</span></td>
                    <td className="p-3">{d ? <span className="text-[10px] uppercase font-bold" style={{ color: d.color }} data-testid={`tw-log-delivery-${l.id}`}>{d.label}{l.error_code ? ` · ${l.error_code}` : ""}</span> : <span className="text-[10px] text-[#868E96]">—</span>}</td>
                    <td className="p-3 text-right">
                      {canResend && (
                        <Button size="sm" variant="outline" onClick={() => resendOne(l.id)} disabled={busy === l.id || !cfg?.enabled} data-testid={`tw-log-resend-${l.id}`} className="h-7 text-[10px] uppercase tracking-wider">
                          <Send className="h-3 w-3 mr-1" />Renvoyer
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
